import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CreditCard, TrendingUp, DollarSign, Calendar, AlertTriangle, Edit, Save, X, Download } from "lucide-react";
import { transactionsService, Transaction } from "@/api/transactionsService";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

interface UserTransactionHistoryProps {
  userId: string;
}

const UserTransactionHistory: React.FC<UserTransactionHistoryProps> = ({ userId }) => {
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const [editingTransaction, setEditingTransaction] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<{
    credits: string;
    value: string;
    status: string;
  }>({ credits: "", value: "", status: "" });

  const {
    data: transactionsData,
    isLoading,
    error,
    isError,
  } = useQuery({
    queryKey: ["user-transactions", userId],
    queryFn: async () => {
      // Fetch all transactions and filter for this user
      const allTransactions = await transactionsService.getTransactions(1, 1000);
      const userTransactions = allTransactions.transactions.filter(
        transaction => transaction.user_id === userId
      );
      return {
        transactions: userTransactions,
        total: userTransactions.length
      };
    },
    refetchOnWindowFocus: false,
    retry: 1,
  });

  // Update transaction mutation
  const updateTransactionMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: { credits?: number; value?: number; status?: "paid" | "pending" } }) => {
      return await transactionsService.updateTransaction(id, updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-transactions", userId] });
      queryClient.invalidateQueries({ queryKey: ["transactions"] });
      setEditingTransaction(null);
      toast.success("Transaction updated successfully");
    },
    onError: (error) => {
      console.error("Error updating transaction:", error);
      toast.error("Failed to update transaction");
    },
  });

  const transactions = transactionsData?.transactions || [];
  const totalCredits = transactions.reduce((sum, t) => sum + t.credits, 0);
  const totalValue = transactions.reduce((sum, t) => sum + t.value, 0);
  const paidTransactions = transactions.filter(t => t.status === "paid");
  const pendingTransactions = transactions.filter(t => t.status === "pending");

  const isAdmin = currentUser?.role === "admin";

  const startEditing = (transaction: Transaction) => {
    setEditingTransaction(transaction.id);
    setEditValues({
      credits: transaction.credits.toString(),
      value: transaction.value.toString(),
      status: transaction.status,
    });
  };

  const cancelEditing = () => {
    setEditingTransaction(null);
    setEditValues({ credits: "", value: "", status: "" });
  };

  const saveTransaction = (transactionId: string) => {
    const credits = parseFloat(editValues.credits);
    const value = parseFloat(editValues.value);
    
    if (isNaN(credits) || isNaN(value)) {
      toast.error("Please enter valid numbers for credits and value");
      return;
    }

    updateTransactionMutation.mutate({
      id: transactionId,
      updates: {
        credits,
        value,
        status: editValues.status as "paid" | "pending",
      },
    });
  };

  const exportTransactionsCSV = () => {
    if (transactions.length === 0) {
      toast.error("No transactions to export");
      return;
    }

    const exportData = transactions.map(transaction => ({
      Date: format(new Date(transaction.created_at), "MMM dd, yyyy"),
      Time: format(new Date(transaction.created_at), "HH:mm"),
      Credits: transaction.credits,
      "Value (£)": transaction.value.toFixed(2),
      Status: transaction.status,
      "Transaction ID": transaction.id,
    }));

    const csvContent = [
      Object.keys(exportData[0]).join(','),
      ...exportData.map(row => 
        Object.values(row).map(value => 
          typeof value === 'string' && value.includes(',') ? `"${value}"` : value
        ).join(',')
      )
    ].join('\n');
    
    const csvBlob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const csvUrl = URL.createObjectURL(csvBlob);
    const csvLink = document.createElement('a');
    csvLink.href = csvUrl;
    csvLink.setAttribute('download', `carbon_credit_transactions_${userId}_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(csvLink);
    csvLink.click();
    document.body.removeChild(csvLink);
    URL.revokeObjectURL(csvUrl);

    toast.success("Transactions exported successfully");
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid gap-4 md:grid-cols-3">
          <Skeleton className="h-24 w-full rounded-lg" />
          <Skeleton className="h-24 w-full rounded-lg" />
          <Skeleton className="h-24 w-full rounded-lg" />
        </div>
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-48" />
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {Array.from({ length: 5 }).map((_, index) => (
                <div key={index} className="flex justify-between">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-6 w-16" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isError) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <div className="rounded-full bg-red-100 p-3">
            <AlertTriangle className="h-6 w-6 text-red-600" />
          </div>
          <h3 className="mt-4 text-lg font-medium">Error Loading Transactions</h3>
          <p className="mt-2 text-sm text-muted-foreground">
            {error instanceof Error ? error.message : "Unknown error occurred"}
          </p>
        </CardContent>
      </Card>
    );
  }

  const getStatusBadge = (status: string) => {
    switch (status.toLowerCase()) {
      case "paid":
        return <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Paid</Badge>;
      case "pending":
        return <Badge variant="outline" className="border-yellow-500 text-yellow-700">Pending</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardContent className="flex items-center gap-4 p-6">
            <div className="rounded-full bg-blue-100 p-3">
              <CreditCard className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Total Credits</p>
              <p className="text-2xl font-bold">{totalCredits.toFixed(2)}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-center gap-4 p-6">
            <div className="rounded-full bg-green-100 p-3">
              <DollarSign className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Total Value</p>
              <p className="text-2xl font-bold">£{totalValue.toFixed(2)}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-center gap-4 p-6">
            <div className="rounded-full bg-purple-100 p-3">
              <TrendingUp className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Transactions</p>
              <p className="text-2xl font-bold">{transactions.length}</p>
              <p className="text-xs text-muted-foreground">
                {paidTransactions.length} paid, {pendingTransactions.length} pending
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Transactions Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Carbon Credit Transaction History
            </CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={exportTransactionsCSV}
              disabled={transactions.length === 0}
              className="flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              Export CSV
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {transactions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="rounded-full bg-muted p-4">
                <CreditCard className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="mt-4 text-lg font-medium">No Transactions Found</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                This user hasn't made any carbon credit transactions yet.
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Credits</TableHead>
                  <TableHead>Value</TableHead>
                  <TableHead>Status</TableHead>
                  {isAdmin && <TableHead>Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {transactions.map((transaction) => {
                  const isEditing = editingTransaction === transaction.id;
                  
                  return (
                    <TableRow key={transaction.id} className="hover:bg-muted/50">
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <p className="font-medium">
                              {format(new Date(transaction.created_at), "MMM dd, yyyy")}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {format(new Date(transaction.created_at), "HH:mm")}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {isEditing ? (
                          <Input
                            type="number"
                            step="0.01"
                            value={editValues.credits}
                            onChange={(e) => setEditValues(prev => ({ ...prev, credits: e.target.value }))}
                            className="w-20"
                          />
                        ) : (
                          <div className="flex items-center gap-1">
                            <span className="font-medium">{transaction.credits}</span>
                            <span className="text-sm text-muted-foreground">credits</span>
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        {isEditing ? (
                          <div className="flex items-center gap-1">
                            <span>£</span>
                            <Input
                              type="number"
                              step="0.01"
                              value={editValues.value}
                              onChange={(e) => setEditValues(prev => ({ ...prev, value: e.target.value }))}
                              className="w-20"
                            />
                          </div>
                        ) : (
                          <span className="font-medium">£{transaction.value.toFixed(2)}</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {isEditing ? (
                          <Select
                            value={editValues.status}
                            onValueChange={(value) => setEditValues(prev => ({ ...prev, status: value }))}
                          >
                            <SelectTrigger className="w-24">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="paid">Paid</SelectItem>
                              <SelectItem value="pending">Pending</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          getStatusBadge(transaction.status)
                        )}
                      </TableCell>
                      {isAdmin && (
                        <TableCell>
                          {isEditing ? (
                            <div className="flex items-center gap-1">
                              <Button
                                size="sm"
                                onClick={() => saveTransaction(transaction.id)}
                                disabled={updateTransactionMutation.isPending}
                              >
                                <Save className="h-3 w-3" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={cancelEditing}
                                disabled={updateTransactionMutation.isPending}
                              >
                                <X className="h-3 w-3" />
                              </Button>
                            </div>
                          ) : (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => startEditing(transaction)}
                            >
                              <Edit className="h-3 w-3" />
                            </Button>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default UserTransactionHistory; 